/**
 * Banners Management Page (Multi-language Support)
 *
 * Admin interface for creating, viewing, and managing system banners.
 * Supports 11 languages with fallback to English.
 * Issue #242 - System banner management
 */

import { useEffect, useMemo, useState } from 'react';
import { HelpTooltip } from '../components/HelpTooltip';
import {
  apiClient,
  type Banner,
  type CreateBannerPayload,
  type MultiLangText,
  type SupportedLanguage,
  type UpdateBannerPayload
} from '../lib/api-client';

/**
 * Remove empty/whitespace-only entries from a multi-language text object.
 * Ensures only meaningful translations are saved.
 */
function cleanMultiLangText(obj: MultiLangText): MultiLangText {
  const cleaned: MultiLangText = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value && value.trim().length > 0) {
      cleaned[key as SupportedLanguage] = value;
    }
  }
  return cleaned;
}

/**
 * Format a date string to 'YYYY-MM-DD HH:mm' in the user's local timezone.
 */
function formatDateTime(dateStr: string): string {
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dateStr));
}

interface Stats {
  totalCount: number;
  activeCount: number;
  scheduledCount: number;
  expiredCount: number;
}

const SUPPORTED_LANGUAGES: Array<{ code: SupportedLanguage; name: string; flag: string }> = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'zh-cn', name: '简体中文', flag: '🇨🇳' },
  { code: 'zh-tw', name: '繁體中文', flag: '🇹🇼' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' }
];

export function BannersPage() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [filterLevel, setFilterLevel] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [activeTab, setActiveTab] = useState<SupportedLanguage>('en');

  // Language code to name mapping for efficient lookup
  const langNameMap = useMemo(
    () => new Map(SUPPORTED_LANGUAGES.map(({ code, name }) => [code, name])),
    []
  );

  // Multi-language form state
  const [formData, setFormData] = useState({
    title: {} as Record<string, string>,
    message: {} as Record<string, string>,
    linkUrl: {} as Record<string, string>,
    linkText: {} as Record<string, string>,
    level: 'INFO' as Banner['level'],
    priority: 0,
    hiddenLanguages: [] as string[],
    startAt: '',
    endAt: ''
  });

  useEffect(() => {
    void fetchBanners();
    void fetchStats();
  }, [includeInactive]);

  const fetchBanners = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiClient.getBanners({ includeInactive });
      if (Array.isArray(data.banners)) {
        setBanners(data.banners);
      } else {
        setBanners([]);
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setBanners([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiClient.getBannerStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: {},
      message: {},
      linkUrl: {},
      linkText: {},
      level: 'INFO',
      priority: 0,
      hiddenLanguages: [],
      startAt: '',
      endAt: ''
    });
    setEditingBanner(null);
    setActiveTab('en');
  };

  const validateForm = (): boolean => {
    if (!formData.title.en?.trim() || !formData.message.en?.trim()) {
      setError('English (en) title and message are required and cannot be empty.');
      return false;
    }
    return true;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      const startDate = new Date(formData.startAt);
      const endDate = new Date(formData.endAt);

      const cleanedLinkUrl = cleanMultiLangText(formData.linkUrl);
      const cleanedLinkText = cleanMultiLangText(formData.linkText);

      const payload: CreateBannerPayload = {
        title: cleanMultiLangText(formData.title),
        message: cleanMultiLangText(formData.message),
        level: formData.level,
        priority: formData.priority,
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
        hiddenLanguages: formData.hiddenLanguages
      };

      // Add link if provided for any language
      if (Object.keys(cleanedLinkUrl).length > 0) {
        payload.linkUrl = cleanedLinkUrl;
        payload.linkText =
          Object.keys(cleanedLinkText).length > 0 ? cleanedLinkText : { en: 'Learn more' };
      }

      await apiClient.createBanner(payload);

      resetForm();
      setShowCreateModal(false);
      void fetchBanners();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create banner');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBanner) return;

    setError(null);

    if (!validateForm()) return;

    try {
      const startDate = new Date(formData.startAt);
      const endDate = new Date(formData.endAt);

      const cleanedLinkUrl = cleanMultiLangText(formData.linkUrl);
      const cleanedLinkText = cleanMultiLangText(formData.linkText);

      const payload: UpdateBannerPayload = {
        title: cleanMultiLangText(formData.title),
        message: cleanMultiLangText(formData.message),
        level: formData.level,
        priority: formData.priority,
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
        hiddenLanguages: formData.hiddenLanguages
      };

      if (Object.keys(cleanedLinkUrl).length > 0) {
        payload.linkUrl = cleanedLinkUrl;
        payload.linkText =
          Object.keys(cleanedLinkText).length > 0 ? cleanedLinkText : { en: 'Learn more' };
      } else {
        payload.linkUrl = null;
        payload.linkText = null;
      }

      await apiClient.updateBanner(editingBanner.id, payload);

      resetForm();
      void fetchBanners();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update banner');
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this banner?')) {
      return;
    }

    try {
      await apiClient.deactivateBanner(id);
      void fetchBanners();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate banner');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this banner? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.deleteBanner(id);
      void fetchBanners();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete banner');
    }
  };

  const openEditModal = (banner: Banner) => {
    setEditingBanner(banner);

    const startDate = new Date(banner.start_at);
    const endDate = new Date(banner.end_at);

    const formatDatetimeLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setFormData({
      title: banner.title as Record<string, string>,
      message: banner.message as Record<string, string>,
      linkUrl: (banner.link_url as Record<string, string>) || {},
      linkText: (banner.link_text as Record<string, string>) || {},
      level: banner.level,
      priority: banner.priority,
      hiddenLanguages: banner.hidden_languages || [],
      startAt: formatDatetimeLocal(startDate),
      endAt: formatDatetimeLocal(endDate)
    });
  };

  const updateFormField = (
    field: 'title' | 'message' | 'linkUrl' | 'linkText',
    lang: SupportedLanguage,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const toggleHiddenLanguage = (lang: string) => {
    setFormData(prev => ({
      ...prev,
      hiddenLanguages: prev.hiddenLanguages.includes(lang)
        ? prev.hiddenLanguages.filter(l => l !== lang)
        : [...prev.hiddenLanguages, lang]
    }));
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      INFO: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      WARNING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      ERROR: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return colors[level as keyof typeof colors] || colors.INFO;
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      SCHEDULED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      EXPIRED: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      INACTIVE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status as keyof typeof colors] || colors.INACTIVE;
  };

  const filteredBanners = banners.filter(banner => {
    if (filterLevel !== 'ALL' && banner.level !== filterLevel) return false;
    if (filterStatus !== 'ALL' && banner.status !== filterStatus) return false;
    return true;
  });

  // Get banner display text (English is required, always use en)
  const getBannerDisplayText = (banner: Banner, field: 'title' | 'message'): string => {
    const data = field === 'title' ? banner.title : banner.message;
    return data.en ?? '';
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Banners</h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Manage system-wide banners and announcements (Multi-language)
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700'
        >
          Create Banner
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className='grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4'>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>Total</p>
            <p className='mt-1 text-xl font-bold text-gray-900 md:mt-2 md:text-3xl dark:text-white'>
              {stats.totalCount}
            </p>
          </div>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>
              Active
            </p>
            <p className='mt-1 text-xl font-bold text-green-600 md:mt-2 md:text-3xl dark:text-green-400'>
              {stats.activeCount}
            </p>
          </div>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>
              Scheduled
            </p>
            <p className='mt-1 text-xl font-bold text-blue-600 md:mt-2 md:text-3xl dark:text-blue-400'>
              {stats.scheduledCount}
            </p>
          </div>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>
              Expired
            </p>
            <p className='mt-1 text-xl font-bold text-gray-600 md:mt-2 md:text-3xl dark:text-gray-400'>
              {stats.expiredCount}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className='flex flex-wrap items-center gap-4 rounded-lg bg-white p-4 shadow dark:bg-gray-800'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Level
          </label>
          <select
            value={filterLevel}
            onChange={e => setFilterLevel(e.target.value)}
            className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value='ALL'>All Levels</option>
            <option value='INFO'>Info</option>
            <option value='WARNING'>Warning</option>
            <option value='ERROR'>Error</option>
            <option value='SUCCESS'>Success</option>
          </select>
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Status
          </label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value='ALL'>All Status</option>
            <option value='ACTIVE'>Active</option>
            <option value='SCHEDULED'>Scheduled</option>
            <option value='EXPIRED'>Expired</option>
            <option value='INACTIVE'>Inactive</option>
          </select>
        </div>

        <div className='flex items-center'>
          <input
            type='checkbox'
            id='includeInactive'
            checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)}
            className='mr-2 h-4 w-4 rounded border-gray-300'
          />
          <label htmlFor='includeInactive' className='text-sm text-gray-700 dark:text-gray-300'>
            Include inactive banners
          </label>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className='rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* Banners List */}
      <div className='rounded-lg bg-white shadow dark:bg-gray-800'>
        {isLoading ? (
          <div className='flex items-center justify-center p-12'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent'></div>
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className='p-12 text-center text-gray-500 dark:text-gray-400'>No banners found</div>
        ) : (
          <div className='divide-y divide-gray-200 dark:divide-gray-700'>
            {filteredBanners.map(banner => (
              <div key={banner.id} className='p-4 hover:bg-gray-50 dark:hover:bg-gray-700'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-semibold text-gray-900 dark:text-white'>
                        {getBannerDisplayText(banner, 'title')}
                      </h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getLevelBadge(banner.level)}`}
                      >
                        {banner.level}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(banner.status)}`}
                      >
                        {banner.status}
                      </span>
                      <span className='text-xs text-gray-500'>Priority: {banner.priority}</span>
                    </div>
                    <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                      {getBannerDisplayText(banner, 'message')}
                    </p>
                    <div className='mt-2 flex items-center gap-2 text-xs text-gray-500'>
                      <span>Languages: {Object.keys(banner.title).join(', ')}</span>
                      {banner.hidden_languages?.length > 0 && (
                        <span>| Hidden: {banner.hidden_languages?.join(', ')}</span>
                      )}
                    </div>
                    <div className='mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500'>
                      <span>Start: {formatDateTime(banner.start_at)}</span>
                      <span>End: {formatDateTime(banner.end_at)}</span>
                      <span>Created: {formatDateTime(banner.created_at)}</span>
                    </div>
                  </div>
                  <div className='ml-4 flex gap-2'>
                    <button
                      onClick={() => openEditModal(banner)}
                      className='rounded-lg p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20'
                    >
                      Edit
                    </button>
                    {banner.is_active && banner.status !== 'EXPIRED' && (
                      <button
                        onClick={() => handleDeactivate(banner.id)}
                        className='rounded-lg p-2 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20'
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(banner.id)}
                      className='rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingBanner) && (
        <div className='bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4'>
          <div className='max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
              {editingBanner ? 'Edit Banner' : 'Create Banner'}
            </h2>

            <form onSubmit={editingBanner ? handleUpdate : handleCreate} className='mt-6 space-y-6'>
              {/* Language Tabs */}
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Languages
                  <HelpTooltip content='Select a language tab to enter content for that language. English (en) is required as fallback.' />
                </label>
                <div className='flex flex-wrap gap-2 border-b border-gray-200 pb-2 dark:border-gray-700'>
                  {SUPPORTED_LANGUAGES.map(({ code, name, flag }) => (
                    <button
                      key={code}
                      type='button'
                      onClick={() => setActiveTab(code)}
                      className={`rounded-t-lg px-3 py-2 text-sm font-medium transition-colors ${
                        activeTab === code
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      } ${code === 'en' ? 'ring-2 ring-yellow-400' : ''}`}
                    >
                      {flag} {name}
                      {code === 'en' && ' *'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title for selected language */}
              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Title ({activeTab}) {activeTab === 'en' && '*'}
                </label>
                <input
                  type='text'
                  required={activeTab === 'en'}
                  maxLength={200}
                  value={formData.title[activeTab] || ''}
                  onChange={e => updateFormField('title', activeTab, e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Enter title in ${langNameMap.get(activeTab)}`}
                />
              </div>

              {/* Message for selected language */}
              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Message ({activeTab}) {activeTab === 'en' && '*'}
                </label>
                <textarea
                  required={activeTab === 'en'}
                  maxLength={1000}
                  rows={4}
                  value={formData.message[activeTab] || ''}
                  onChange={e => updateFormField('message', activeTab, e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Enter message in ${langNameMap.get(activeTab)}`}
                />
              </div>

              {/* Link URL for selected language */}
              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Link URL ({activeTab}) (optional)
                </label>
                <input
                  type='url'
                  value={formData.linkUrl[activeTab] || ''}
                  onChange={e => updateFormField('linkUrl', activeTab, e.target.value)}
                  placeholder={`https://example.com/${activeTab}/article`}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                />
              </div>

              {/* Link Text for selected language */}
              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Link Text ({activeTab}) (optional)
                </label>
                <input
                  type='text'
                  maxLength={100}
                  value={formData.linkText[activeTab] || ''}
                  onChange={e => updateFormField('linkText', activeTab, e.target.value)}
                  placeholder='Learn more'
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                />
              </div>

              {/* Separator: Per-language settings above / Common settings below */}
              <div className='relative py-2'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-gray-300 dark:border-gray-600' />
                </div>
                <div className='relative flex justify-center'>
                  <span className='bg-white px-3 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400'>
                    Common Settings
                  </span>
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                {/* Level */}
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Level
                  </label>
                  <select
                    value={formData.level}
                    onChange={e =>
                      setFormData({ ...formData, level: e.target.value as Banner['level'] })
                    }
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  >
                    <option value='INFO'>Info</option>
                    <option value='WARNING'>Warning</option>
                    <option value='ERROR'>Error</option>
                    <option value='SUCCESS'>Success</option>
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Priority (0-1000)
                    <HelpTooltip content='Higher numbers = more important. Banners are displayed in priority order (highest first).' />
                  </label>
                  <input
                    type='number'
                    min={0}
                    max={1000}
                    value={formData.priority}
                    onChange={e =>
                      setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 0 })
                    }
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                </div>
              </div>

              {/* Hidden Languages */}
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Hidden Languages
                  <HelpTooltip content='Select languages where this banner should NOT be displayed. Users with these languages will not see this banner at all (no fallback to English).' />
                </label>
                <div className='grid grid-cols-2 gap-2 md:grid-cols-4'>
                  {SUPPORTED_LANGUAGES.map(({ code, name, flag }) => (
                    <label key={code} className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        checked={formData.hiddenLanguages.includes(code)}
                        onChange={() => toggleHiddenLanguage(code)}
                        className='h-4 w-4 rounded border-gray-300'
                      />
                      <span className='text-sm text-gray-700 dark:text-gray-300'>
                        {flag} {name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                {/* Start Date & Time */}
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Start Date & Time *
                  </label>
                  <input
                    type='datetime-local'
                    required
                    value={formData.startAt}
                    onChange={e => setFormData({ ...formData, startAt: e.target.value })}
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                </div>

                {/* End Date & Time */}
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    End Date & Time *
                  </label>
                  <input
                    type='datetime-local'
                    required
                    value={formData.endAt}
                    onChange={e => setFormData({ ...formData, endAt: e.target.value })}
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                </div>
              </div>

              <div className='flex justify-end gap-2 pt-4'>
                <button
                  type='button'
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(false);
                  }}
                  className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700'
                >
                  {editingBanner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
